import { useTranslation } from "react-i18next";
import {
  AiOutlineEye,
  AiOutlineFile,
  AiOutlineFileExcel,
  AiOutlineFilePdf,
  AiOutlineFileText,
  AiOutlineFileWord,
  AiOutlinePicture,
} from "react-icons/ai";
import styles from "./ChatFileCard.module.scss";

/**
 * File attachment card for a chat message (PDF, Word, Excel, CSV, …): a document "thumbnail"
 * on a soft tint of the type colour with a type badge, then the file name, "PDF · 236 KB" and a
 * view button. Clicking it calls `onOpen` (the chat opens the in-page AttachmentPreview).
 *
 * Also exports the helpers the popup and the "file ready to send" card share: fileTypeOf,
 * formatFileSize, attachmentDetails and FileTypeTile.
 *
 * The same files live in every portal's chat (lab, imaging, physio, nurse, insurance,
 * doctor / pharmacy): ChatFileCard.jsx/.module.scss, AttachmentPreview.jsx/.module.scss and
 * StagedAttachment.jsx/.module.scss. Keep the copies identical so attachments look the same
 * everywhere (the design follows the admin panel's chat).
 *
 * Props: fileName, contentType, fileSize (bytes), mine (sent by the current user),
 *        disabled, onOpen
 */

// Icon, colour and short type label per file type.
const FILE_TYPES = {
  pdf: { label: "PDF", Icon: AiOutlineFilePdf, color: "#dc2626", tint: "#fee2e2" },
  word: { label: "Word", Icon: AiOutlineFileWord, color: "#2563eb", tint: "#dbeafe" },
  excel: { label: "Excel", Icon: AiOutlineFileExcel, color: "#16a34a", tint: "#dcfce7" },
  csv: { label: "CSV", Icon: AiOutlineFileExcel, color: "#16a34a", tint: "#dcfce7" },
  text: { label: "TXT", Icon: AiOutlineFileText, color: "#4b5563", tint: "#f3f4f6" },
  other: { label: "", Icon: AiOutlineFile, color: "#077272", tint: "#e0f7f7" },
};

/** Lower-case extension of a file name or URL ("" when there is none). */
export const extensionOf = (name = "") => {
  const clean = String(name || "").split("?")[0];
  const slash = clean.lastIndexOf("/");
  const base = slash >= 0 ? clean.slice(slash + 1) : clean;
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : "";
};

export const fileTypeOf = (fileName = "", contentType = "") => {
  const ext = extensionOf(fileName);
  const mime = String(contentType || "").toLowerCase();
  if (ext === "pdf" || mime === "application/pdf") return FILE_TYPES.pdf;
  if (["doc", "docx"].includes(ext) || mime.includes("word")) return FILE_TYPES.word;
  if (["xls", "xlsx"].includes(ext) || mime.includes("sheet") || mime.includes("excel")) {
    return FILE_TYPES.excel;
  }
  if (ext === "csv" || mime === "text/csv") return FILE_TYPES.csv;
  if (ext === "txt" || mime === "text/plain") return FILE_TYPES.text;
  return { ...FILE_TYPES.other, label: ext && ext.length <= 5 ? ext.toUpperCase() : "" };
};

/** "245 KB" / "1.2 MB"; empty when the size is unknown. */
export const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (!n || n < 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/** Short type label of an image: "PNG", "JPG", "WEBP"… ("" when unknown). */
const imageTypeLabel = (fileName, contentType) => {
  const mime = String(contentType || "").toLowerCase();
  const sub = (mime.startsWith("image/") ? mime.slice(6) : extensionOf(fileName)).replace("+xml", "");
  if (sub === "jpeg") return "JPG";
  return sub && sub.length <= 5 ? sub.toUpperCase() : "";
};

/** The attachment's caption line: "PDF · 236 KB", "PNG · 1.2 MB", "Word"… */
export const attachmentDetails = ({ fileName, contentType, fileSize, isImage = false }) => {
  const label = isImage ? imageTypeLabel(fileName, contentType) : fileTypeOf(fileName, contentType).label;
  return [label, formatFileSize(fileSize)].filter(Boolean).join(" · ");
};

/** Coloured square with the file type ("PDF", "DOCX", …), or a picture icon for images. */
export const FileTypeTile = ({ fileName, contentType, isImage = false }) => {
  if (isImage) {
    return (
      <span className={`${styles.typeTile} ${styles.typeTileImage}`} aria-hidden="true">
        <AiOutlinePicture size={20} />
      </span>
    );
  }
  const type = fileTypeOf(fileName, contentType);
  const ext = extensionOf(fileName);
  const label = ext && ext.length <= 4 ? ext.toUpperCase() : type.label.length <= 4 ? type.label.toUpperCase() : "";
  const Icon = type.Icon;
  return (
    <span className={styles.typeTile} style={{ backgroundColor: type.color }} aria-hidden="true">
      {label || <Icon size={20} />}
    </span>
  );
};

const ChatFileCard = ({ fileName, contentType, fileSize, mine = false, disabled = false, onOpen }) => {
  const { t } = useTranslation();
  const name = fileName || t("chat.preview.attachment", "Attachment");
  const fileType = fileTypeOf(name, contentType);
  const meta = attachmentDetails({ fileName: name, contentType, fileSize });
  const FileIcon = fileType.Icon;

  return (
    <button
      type="button"
      className={`${styles.fileCard} ${mine ? styles.fileCardMine : ""}`}
      onClick={onOpen}
      disabled={disabled}
      title={name}
    >
      <span
        className={styles.fileCardPreview}
        style={{ background: `linear-gradient(135deg, ${fileType.tint} 0%, #ffffff 130%)` }}
        aria-hidden="true"
      >
        <span className={styles.fileCardPage} style={{ color: fileType.color }}>
          <FileIcon size={24} />
        </span>
        {fileType.label && (
          <span className={styles.fileCardBadge} style={{ backgroundColor: fileType.color }}>
            {fileType.label}
          </span>
        )}
      </span>
      <span className={styles.fileCardFooter}>
        <span className={styles.fileCardBody}>
          <span className={styles.fileCardName}>{name}</span>
          {meta && <span className={styles.fileCardMeta}>{meta}</span>}
        </span>
        <span className={styles.fileCardAction} aria-hidden="true">
          <AiOutlineEye size={16} />
        </span>
      </span>
    </button>
  );
};

/** Style for the message bubble around a file card: no padding or colour, so the card is the message. */
export const FILE_BUBBLE_STYLE = { padding: 0, background: "transparent" };

export default ChatFileCard;
